import * as A from '../clients/mira/types/Application';
import * as P from '../clients/mira/types/Presentation';

// OAuth presentation-property types.
//
// Source of truth is raydiant-kit's authoring-side extractProperties
// (node_modules/raydiant-kit/lib/prop-types/extractProperties.js — the
// `oAuth | facebookAuth | googleAuth | onedriveAuth | postermywallAuth` branch).
// raydiant-kit only exposes these as authoring-side prop-type classes, not as a
// consumable data set, so the list is mirrored here for the consuming side.
// `oAuth2` is included for forward-compatibility (PresentationForm already
// renders an `oAuth2` case). Keep in sync if raydiant-kit adds an OAuth type.
export const OAUTH_PROP_TYPES = [
  'oAuth',
  'oAuth2',
  'facebookAuth',
  'googleAuth',
  'onedriveAuth',
  'postermywallAuth',
] as const;

export type OAuthPropType = typeof OAUTH_PROP_TYPES[number];

// True when a presentation property is an actionable OAuth credential prop.
// Hidden props (`hide === true`) are excluded: multi-source apps (e.g. presenter)
// declare OAuth props that are conditionally hidden, and prefill/write must never
// touch a hidden prop (H5 invariant).
export const isOAuthProp = (prop: P.PresentationProperty): boolean =>
  (OAUTH_PROP_TYPES as readonly string[]).includes(prop.type) &&
  prop.hide !== true;

// The actionable OAuth props declared by an app version's published schema.
// Empty when the app has none (the common case).
export const getOAuthProps = (
  appVersion: Pick<A.ApplicationVersion, 'presentationProperties'>,
): P.PresentationProperty[] =>
  (appVersion.presentationProperties ?? []).filter(isOAuthProp);

// True when an app version declares at least one OAuth prop.
export const hasOAuthProps = (
  appVersion: Pick<A.ApplicationVersion, 'presentationProperties'>,
): boolean => getOAuthProps(appVersion).length > 0;

// Overlay verified prefill values onto a presentation's OAuth props.
//
// fill-if-empty: a prop is filled only when its current applicationVariable is
// empty (null / undefined / ''). This single rule gives "fresh-OAuth-wins" for
// free — a value the user just OAuth'd is non-empty, so prefill never clobbers
// it. `prefill` is keyed by prop name and contains ONLY already-verified values
// (the saga verifies before dispatching). Hidden props are excluded via
// getOAuthProps. Returns the same reference when nothing changes (memo-friendly).
export const overlayOAuthPrefill = (
  presentation: P.Presentation,
  prefill: { [propName: string]: string } | undefined,
): P.Presentation => {
  if (!prefill) return presentation;

  let applicationVariables = presentation.applicationVariables;
  let changed = false;

  for (const prop of getOAuthProps(presentation)) {
    const value = prefill[prop.name];
    if (value === undefined || value === null || value === '') continue;

    const current = applicationVariables?.[prop.name];
    const isEmpty = current === null || current === undefined || current === '';
    if (!isEmpty) continue;

    if (!changed) {
      applicationVariables = { ...applicationVariables };
      changed = true;
    }
    applicationVariables[prop.name] = value;
  }

  return changed ? { ...presentation, applicationVariables } : presentation;
};
